import { ArrowDownTrayIcon, PaperClipIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';

import { MessageAttachment } from '@/services/types/artifact-types';

interface AttachmentListProps {
  attachments: MessageAttachment[];
  isExpanded?: boolean;
  showCount?: boolean;
}

const getFileTypeIcon = (contentType: string | null) => {
  if (!contentType) return PaperClipIcon;

  if (contentType.startsWith('image/')) return '🖼️';
  if (contentType.includes('pdf')) return '📄';
  if (contentType.includes('word') || contentType.includes('document'))
    return '📝';
  if (contentType.includes('excel') || contentType.includes('spreadsheet'))
    return '📊';
  if (contentType.includes('zip') || contentType.includes('archive'))
    return '📦';

  return PaperClipIcon;
};

const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const AttachmentList = ({
  attachments,
  isExpanded = false,
  showCount = false,
}: AttachmentListProps) => {
  if (!attachments?.length) return null;

  // Quick indicator when collapsed
  if (!isExpanded && showCount) {
    return (
      <div className="flex items-center gap-1.5 mt-3 text-xs text-gray-500">
        <PaperClipIcon className="h-3.5 w-3.5" />
        <span className="font-medium">
          {attachments.length} attachment{attachments.length > 1 ? 's' : ''}
        </span>
      </div>
    );
  }

  // Full attachment list when expanded
  return (
    <div>
      <h4 className="text-xs font-semibold text-gray-700 mb-3 uppercase tracking-wide flex items-center gap-1.5">
        <PaperClipIcon className="h-3.5 w-3.5" />
        Attachments ({attachments.length})
      </h4>
      <div className="space-y-2">
        {attachments.map((attachment) => {
          const FileIcon = getFileTypeIcon(attachment.content_type);
          const isIconString = typeof FileIcon === 'string';

          return (
            <a
              key={attachment.id}
              href={attachment.blob?.download_url || '#'}
              target="_blank"
              rel="noopener noreferrer"
              className={clsx(
                'group flex items-center gap-3 p-3 rounded-lg border border-gray-200',
                'bg-gradient-to-r from-gray-50 to-white',
                'hover:from-blue-50 hover:to-blue-25 hover:border-blue-200',
                'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1',
                'transition-all duration-200 hover:shadow-sm',
              )}
              aria-label={`Download ${attachment.name || 'attachment'}`}
            >
              {/* File type icon */}
              <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-white border border-gray-200 flex items-center justify-center group-hover:border-blue-300 transition-colors">
                {isIconString ? (
                  <span className="text-lg">{FileIcon}</span>
                ) : (
                  <FileIcon className="h-4 w-4 text-gray-500 group-hover:text-blue-600" />
                )}
              </div>

              {/* File details */}
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm text-gray-900 truncate group-hover:text-blue-900">
                  {attachment.name || 'Unnamed file'}
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                  {attachment.content_type && (
                    <span className="uppercase font-medium">
                      {attachment.content_type.split('/')[1] ||
                        attachment.content_type}
                    </span>
                  )}
                  {attachment.file_size && (
                    <>
                      <span>•</span>
                      <span>{formatFileSize(attachment.file_size)}</span>
                    </>
                  )}
                </div>
              </div>

              {/* Download indicator */}
              <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                <ArrowDownTrayIcon className="h-4 w-4 text-blue-600" />
              </div>
            </a>
          );
        })}
      </div>
    </div>
  );
};
